import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

const Galaxy = () => {
  const pointsRef = useRef<THREE.Points>(null);

  const particlesGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const count = 5000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    
    const arms = 4;
    const armOffsetMax = 0.5;
    const rotationFactor = 5;
    const randomOffset = 1.5;

    for (let i = 0; i < count; i++) {
      // Distance from center (gaussian-like distribution focused at center)
      const distance = Math.pow(Math.random(), 2) * 50; 
      
      // Which arm
      const armOffset = (Math.random() * armOffsetMax) - (armOffsetMax / 2);
      const spinAngle = distance * rotationFactor;
      const armAngle = (i % arms) * ((Math.PI * 2) / arms);
      
      const angle = spinAngle + armAngle + armOffset;
      
      // Scatter
      const scatterX = (Math.random() - 0.5) * randomOffset * (distance / 10);
      const scatterY = (Math.random() - 0.5) * 2; // Flat Y distribution
      const scatterZ = (Math.random() - 0.5) * randomOffset * (distance / 10);

      positions[i * 3] = Math.cos(angle) * distance + scatterX;
      positions[i * 3 + 1] = scatterY;
      positions[i * 3 + 2] = Math.sin(angle) * distance + scatterZ;

      // Color (yellowish at center, blueish at edges)
      const t = Math.min(1, distance / 50);
      const color = new THREE.Color().lerpColors(
        new THREE.Color('#FFF5E6'), // Center
        new THREE.Color('#88CCFF'), // Edge
        t
      );
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geo;
  }, []);

  useFrame((state, delta) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += delta * 0.05;
    }
  });

  return (
    <points ref={pointsRef} geometry={particlesGeo}>
      <pointsMaterial size={0.3} vertexColors={true} transparent opacity={0.8} sizeAttenuation={true} />
    </points>
  );
};

const CameraController = ({ setZoomedOut }: { setZoomedOut: (val: boolean) => void }) => {
  useFrame((state) => {
    // 30 second loop (0 to 30)
    const time = state.clock.getElapsedTime() % 30;
    
    // Zoom out over 25 seconds, hold for 5, then snap back
    if (time < 25) {
      // Easing out
      const t = time / 25;
      // start at 20, end at 120
      const z = 20 + t * t * 100;
      state.camera.position.set(0, z * 0.3, z);
      setZoomedOut(t > 0.8);
    } else {
      setZoomedOut(true);
    }
    state.camera.lookAt(0, 0, 0);
  });
  return null;
};

export const MilkyWayFlythrough: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  const [zoomedOut, setZoomedOut] = useState(false);

  // Earth's position in the galaxy roughly halfway out on an arm
  const earthPos: [number, number, number] = [20, 0, 15];

  return (
    <div style={{ width, height, background: '#000000', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
      <Canvas camera={{ position: [0, 6, 20], fov: 60 }}>
        <Galaxy />
        
        {/* Earth Marker */}
        <mesh position={earthPos}>
          <sphereGeometry args={[0.5, 16, 16]} />
          <meshBasicMaterial color="#4A90D9" />
          {zoomedOut && (
            <Html position={[1, 0, 0]} center>
              <div style={{ color: '#4A90D9', whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '14px', textShadow: '0 0 4px black' }}>
                ← You are here
              </div>
            </Html>
          )}
        </mesh>

        <CameraController setZoomedOut={setZoomedOut} />
      </Canvas>
    </div>
  );
};
